import {Component, Input, OnChanges} from '@angular/core';

interface RawAlias {
    platform?: string;
    raw_name?: string;
}

/**
 * Compact, useful rendering of an Organization's aliases. The raw scraped
 * aliases are mostly noise (the same company repeated with province suffixes,
 * sub-unit names and broker boilerplate), so we lead with the genuinely useful
 * signal, the distinct platforms the org appears on, as chips ("Seen on").
 *
 * "Known as" only lists names that are GENUINELY different from the canonical
 * (e.g. an acronym like CJIB vs "Centraal Justitieel Incassobureau"), not
 * trivial variants. For a clean org like Rijkswaterstaat every variant reduces
 * to the canonical, so "Known as" is hidden entirely.
 */
@Component({
    selector: 'bst-organization-aliases',
    templateUrl: './organization-aliases.component.html',
    styleUrls: ['./organization-aliases.component.scss'],
    standalone: false,
})
export class OrganizationAliasesComponent implements OnChanges {

    @Input() aliases: RawAlias[] | null = null;
    @Input() canonicalName: string | null = null;

    public platforms: string[] = [];
    public names: string[] = [];
    public expanded = false;

    ngOnChanges(): void {
        const list = this.aliases ?? [];
        this.platforms = Array.from(new Set(
            list.map(a => (a.platform ?? '').trim()).filter(Boolean)
        )).sort((a, b) => a.localeCompare(b));

        const canonWords = this.coreWords(this.canonicalName ?? '');
        const distinct = Array.from(new Set(
            list.map(a => (a.raw_name ?? '').trim()).filter(Boolean)
        ));
        this.names = distinct
            .filter(n => this.isGenuinelyDifferent(n, canonWords))
            .sort((a, b) => a.localeCompare(b));
    }

    /** Significant words of a name: parentheticals, broker boilerplate and punctuation stripped. */
    private coreWords(s: string): Set<string> {
        const core = (s || '').toLowerCase()
            .replace(/^\s*hr\s*[-–]\s*/, '')
            .replace(/\b(via|t\.?b\.?v\.?|namens)\b.*$/, '')
            .replace(/\(.*?\)/g, ' ')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
        return new Set(core.split(' ').filter(w => w.length > 1));
    }

    /**
     * A name is "genuinely different" only if it is NOT a word-subset/superset
     * of the canonical (those are province variants, sub-units, HR-/via- noise).
     */
    private isGenuinelyDifferent(rawName: string, canonWords: Set<string>): boolean {
        const words = this.coreWords(rawName);
        if (words.size === 0 || canonWords.size === 0) {
            return false;
        }
        const nameSubsetOfCanon = Array.from(words).every(w => canonWords.has(w));
        const canonSubsetOfName = Array.from(canonWords).every(w => words.has(w));
        return !nameSubsetOfCanon && !canonSubsetOfName;
    }
}
